import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  GlobeAltIcon,
  UserGroupIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  HeartIcon,
  SparklesIcon,
  CubeTransparentIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const AboutPage: React.FC = () => {
  const stats = [
    { label: 'Platforms in Ecosystem', value: '50+' },
    { label: 'Countries Served', value: '180+' },
    { label: 'Total Ecosystem Value', value: '$500M+' },
    { label: 'Active Users', value: '1M+' },
  ];

  const values = [
    {
      icon: CubeTransparentIcon,
      title: 'Transparency',
      description: 'Every transaction, every bid, every decision is recorded on-chain for complete auditability.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Security',
      description: 'Bank-grade security with smart contract audits, bug bounties, and continuous monitoring.'
    },
    {
      icon: UserGroupIcon,
      title: 'Community',
      description: 'DAO governance ensures our community shapes the future of the platform together.'
    },
    {
      icon: LightBulbIcon,
      title: 'Innovation',
      description: 'Pioneering new auction mechanisms and DeFi integrations for optimal price discovery.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Accessibility',
      description: 'Global access to high-value auctions without geographic or financial barriers.'
    },
    {
      icon: HeartIcon,
      title: 'Integrity',
      description: 'Ethical business practices and commitment to regulatory compliance worldwide.'
    }
  ];

  const milestones = [
    { year: 'Ene 2025', title: 'La idea nace', description: 'NARDIHA comienza a tomar forma como proyecto. La visión de construir un ecosistema blockchain integrado se empieza a fraguar desde cero.' },
    { year: 'Mar 2025', title: 'Primeros pasos', description: 'Se definen los pilares del ecosistema: gobernanza DAO, tokenomía NDH y los cuatro mecanismos de subasta. Inicio del desarrollo de arquitectura.' },
    { year: 'Sep 2025', title: 'Desarrollo activo', description: 'El equipo construye TEMPLUM DAO. Smart contracts en Solidity, backend en Node.js/PostgreSQL y frontend en React. Contratos auditados internamente.' },
    { year: 'Ene 2026', title: 'Preparación al lanzamiento', description: 'Infraestructura de producción lista. Dominio templum-dao.nardiha.eu configurado. Tests finales y ajustes de seguridad previos al despliegue.' },
    { year: 'Mar 2026', title: 'NFTs en cadena — Ahora', description: 'Esta semana se suben las primeras colecciones propias: Equus Dualis II y Warrior Mythology. Inicio oficial de la plataforma con activos reales.' },
    { year: '2026 →', title: 'Expansión global', description: 'Despliegue multi-cadena (Polygon, Arbitrum, Ethereum), app móvil, integración LayerZero para subastas cross-chain y objetivo de 10.000 usuarios activos.' },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <BuildingOffice2Icon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-primary-400">NARDIHA</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Building the future of decentralized commerce through innovative blockchain solutions that empower users worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-primary-400 font-semibold mb-2 block">Our Mission</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Democratizing Access to High-Value Markets
                </h2>
                <p className="text-gray-300 mb-4">
                  NARDIHA Holdings was founded with a singular vision: to break down the barriers that have traditionally excluded everyday people from participating in high-value markets.
                </p>
                <p className="text-gray-300 mb-4">
                  Through blockchain technology and innovative platform design, we're creating an ecosystem where anyone, anywhere can access opportunities previously reserved for the privileged few.
                </p>
                <p className="text-gray-300">
                  TEMPLUM, our decentralized auction platform, embodies this mission by bringing transparency, efficiency, and global accessibility to the auction industry.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-500/20 to-purple-500/20 rounded-2xl p-8 border border-primary-500/30"
              >
                <RocketLaunchIcon className="w-12 h-12 text-primary-400 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-gray-300">
                  To become the world's leading blockchain ecosystem for decentralized commerce, connecting millions of users through secure, transparent, and innovative platforms that redefine how value is exchanged globally.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-primary-400 font-semibold mb-2 block">Our Values</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                What Drives Us
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, idx) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors"
                  >
                    <Icon className="w-10 h-10 text-primary-400 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">{value.title}</h3>
                    <p className="text-gray-400">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TEMPLUM Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl p-8 md:p-12 border border-primary-500/30">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-primary-400 font-semibold mb-2 block">Platform #20</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    TEMPLUM
                  </h2>
                  <p className="text-gray-300 mb-6">
                    TEMPLUM is our flagship decentralized auction platform, offering four distinct auction mechanisms for optimal price discovery across digital and real-world assets.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-gray-300">
                      <SparklesIcon className="w-5 h-5 text-primary-400" />
                      English, Dutch, Vickrey, and Candle auctions
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <ChartBarIcon className="w-5 h-5 text-primary-400" />
                      Real-time bidding with instant settlement
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <ShieldCheckIcon className="w-5 h-5 text-primary-400" />
                      Audited smart contracts on Polygon
                    </li>
                  </ul>
                  <Link
                    to="/auctions"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
                  >
                    Explore Auctions
                  </Link>
                </div>
                <div className="flex justify-center">
                  <div className="w-64 h-64 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center">
                    <span className="text-6xl font-bold text-white">T</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-primary-400 font-semibold mb-2 block">Our Journey</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Milestones
              </h2>
            </div>
            <div className="space-y-8">
              {milestones.map((milestone, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold">
                      {milestone.year.slice(2)}
                    </div>
                    {idx < milestones.length - 1 && (
                      <div className="w-0.5 h-full bg-dark-700 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <span className="text-primary-400 text-sm">{milestone.year}</span>
                    <h3 className="text-xl font-semibold text-white mb-2">{milestone.title}</h3>
                    <p className="text-gray-400">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-800/50 rounded-2xl p-8 border border-dark-700">
              <h2 className="text-2xl font-bold text-white mb-6">Company Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Legal Entity</h3>
                  <p className="text-gray-400">NARDIHA Holdings S.A.</p>
                  <p className="text-gray-400">Registered in Panama</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Headquarters</h3>
                  <p className="text-gray-400">Panama City, Panama</p>
                  <p className="text-gray-400">Republic of Panama</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Contact</h3>
                  <p className="text-gray-400">info@nardiha.com</p>
                  <p className="text-gray-400">press@nardiha.com</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Social</h3>
                  <div className="flex gap-4">
                    <a href="#" className="text-gray-400 hover:text-primary-400 transition-colors">Twitter</a>
                    <a href="#" className="text-gray-400 hover:text-primary-400 transition-colors">Discord</a>
                    <a href="#" className="text-gray-400 hover:text-primary-400 transition-colors">LinkedIn</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Join Our Journey
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Be part of the revolution in decentralized commerce. Explore opportunities to contribute, invest, or participate in the NARDIHA ecosystem.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/careers"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                View Careers
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
